/**
 * MainBucketedTile — Plan 05-12 Task 1.
 *
 * NEW tile shape: total value + ECharts stacked-bar/area/line + HTML legend + drilldown link.
 * Per UI-SPEC §"MainBucketedTile anatomy".
 *
 * UX-07 4-state machine: loading / cache-warming / sf-unreachable / success
 *
 * Chart rendering: legend is HTML (below chart) so it can wrap responsively +
 * use tabular-nums counts. ECharts legend is disabled (show: false).
 */
import ReactECharts from 'echarts-for-react';

import {
  CacheNotReadyError,
  SalesforceUnavailableError,
} from '../../lib/api-client.js';
import {
  buildEchartsOption,
  CHART_STACK_COLORS,
  type LineByQuarterOptions,
} from '../../lib/echarts-builder.js';
import { formatCompactCurrencyK, formatCountInt, formatCurrency } from '../../lib/format.js';
import { computeSnapDelta } from '../../lib/snap-delta.js';
import { SfUnreachableBanner } from '../SfUnreachableBanner.js';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/Card.js';
import { Skeleton } from '../ui/Skeleton.js';
import { Tooltip } from '../ui/Tooltip.js';

import type { ChartType } from '../../lib/main-tile-mapping.js';
import type { MainGroupedResponse, MainSnapResponse } from '@saledash/shared';

/** Get a color from the chart stack palette (noUncheckedIndexedAccess safe). */
function stackColor(i: number): string {
  return CHART_STACK_COLORS[i % CHART_STACK_COLORS.length] ?? CHART_STACK_COLORS[0] ?? '#1f3fb4';
}

interface MainBucketedTileProps {
  metricId: string;
  title: string;
  /** Chart shape per xlsx Notes column */
  chartType: ChartType;
  /** Optional tooltip formula */
  tooltipFormula?: string | undefined;
  /** Optional drilldown link text */
  drilldownLabel?: string;
  data: MainGroupedResponse | undefined;
  /**
   * Snapshot series for the WoW pill (quick-260701-dea FIX B). Threaded from
   * MainTileSection's `snapData?.snapshots`. Bucketed tiles with no snapshot
   * series (the common case today — no bucketed metric is in
   * SNAP_MAIN_PIPELINE_METRIC_IDS) receive `[]` → computeSnapDelta returns null
   * → NO chip is rendered (never fabricated). Defaults to `[]` for back-compat
   * with callers/tests that don't pass it.
   */
  snapshots?: MainSnapResponse['snapshots'];
  isLoading: boolean;
  isError: boolean;
  error: unknown;
  refetch: () => void;
}

/**
 * Per-metric rendering config for lineByQuarter / unit-aware bucketed tiles
 * (quick-260625-nem Tasks 2 & 4). For these tiles each bucket carries BOTH a
 * `value` (the avg days / avg $) and a `count` (n opps that quarter); the chart
 * must plot `value`, the Y-axis is labeled in the metric's unit, and the
 * headline uses the unit-specific formatter (compact "$49k" / "243 days").
 */
interface QuarterTileConfig {
  valueUnit: 'days' | 'currency' | 'percent';
  yAxisName: string;
  headline: (v: number) => string;
}
const QUARTER_TILE_CONFIG: Record<string, QuarterTileConfig> = {
  // MAIN-49 Marketing-Sourced Win Rate (2026-07-22, John — marketing
  // effectiveness). Each dot = that fiscal quarter's win rate among marketing-
  // sourced closed deals (won / (won+lost)), stored 0..1. Headline = the pooled
  // win rate across the trailing-5-completed-quarter window. `count` per bucket
  // is the closed population (n) behind the rate.
  'MAIN-49': {
    valueUnit: 'percent',
    yAxisName: 'Win rate',
    headline: (v) => `${(v * 100).toFixed(1)}%`,
  },
  // MAIN-20 Sales Cycle Length — each dot = that quarter's avg cycle days.
  'MAIN-20': {
    valueUnit: 'days',
    yAxisName: 'Avg cycle (days)',
    headline: (v) => `${v.toFixed(1)} days`,
  },
  // MAIN-12 Average $ / Closed-Won Deal — each dot = that quarter's avg closed-won
  // $ (relabeled from "Avg $$ / Opportunity", quick-260630-f7f Task 5 item 17; a
  // second open-opp avg line is added — item 18). Headline rounds to nearest $1k
  // and shows compact (48548 → "$49k").
  'MAIN-12': {
    valueUnit: 'currency',
    yAxisName: 'Avg $ / closed-won',
    headline: formatCompactCurrencyK,
  },
};

/**
 * quick-260630-f7f Task 5 (item 18) → rebuilt quick-260630-icb Task 3: build the
 * TWO-line lineByQuarter input for MAIN-12 — the existing Closed-Won avg series
 * plus a second POINT-IN-TIME open-as-of-quarter-end avg series read from each
 * bucket's `openValue`. As of quick-260630-icb the open numbers are no longer a
 * trailing-CloseDate AVG (which returned a near-empty single point); they are the
 * average Amount of opps that were OPEN as of each quarter-end boundary,
 * reconstructed in the sync-worker from OpportunityFieldHistory — so the open line
 * is now DENSE (a snapshot per in-window quarter) rather than sparse.
 *
 * Returns null when no bucket carries `openValue` (older cache rows / non-MAIN-12
 * tiles) so the caller falls back to the single-series path. Quarters with no
 * closed-won data render a null point on the closed-won line (so it doesn't
 * misleadingly dip to $0), while the open line still draws its snapshot there.
 * ECharts renders `null` series points as line gaps.
 */
function buildAvgDualSeriesInput(
  metricId: string,
  buckets: MainGroupedResponse['buckets'],
): { categories: string[]; series: { name: string; values: (number | null)[] }[] } | null {
  const hasOpen = buckets.some((b) => typeof b.openValue === 'number');
  const hasRolling = buckets.some((b) => typeof b.rollingAvg === 'number');
  // Fall back to the single-series path when a tile carries NEITHER an open line
  // (MAIN-12) NOR a rolling line (MAIN-12 / MAIN-20) — e.g. older cache rows.
  if (!hasOpen && !hasRolling) return null;
  const categories = buckets.map((b) => b.label);
  // Primary per-quarter line. MAIN-20 Sales Cycle plots each quarter's avg cycle
  // days ("Avg cycle (days)"); MAIN-12 plots the Closed-Won avg $. A quarter with
  // value 0 plots a null point so the line gaps rather than dropping to 0.
  const primaryName = metricId === 'MAIN-20' ? 'Avg cycle (days)' : 'Closed-Won avg';
  const primary = buckets.map((b) =>
    typeof b.value === 'number' && b.value > 0 ? b.value : null,
  );
  const series: { name: string; values: (number | null)[] }[] = [
    { name: primaryName, values: primary },
  ];
  // Open line (MAIN-12 only): the point-in-time open-as-of-quarter-end avg.
  if (hasOpen) {
    series.push({
      name: 'Open-as-of-quarter-end avg',
      values: buckets.map((b) => (typeof b.openValue === 'number' ? b.openValue : null)),
    });
  }
  // Rolling trailing-5-quarter average line (John, 2026-07-11 for MAIN-12;
  // 2026-07-22 for MAIN-20): each quarter's point is the pooled avg across that
  // quarter + the previous 4.
  if (hasRolling) {
    series.push({
      name: 'Trailing 5-qtr avg',
      values: buckets.map((b) => (typeof b.rollingAvg === 'number' ? b.rollingAvg : null)),
    });
  }
  return { categories, series };
}

/**
 * Per-metric on-tile clarifier subtitle (quick-260630-f7f Task 1 item 1). Renders
 * beneath the context line. MAIN-04 Outbound Activity spells out its composition
 * ("= emails + calls + LinkedIn messages") so the headline number is unambiguous.
 * The metric math is unchanged — this is a label-only deliverable.
 */
const TILE_SUBTITLE_NOTE: Record<string, string> = {
  'MAIN-04': '= emails + calls + LinkedIn messages',
};

/**
 * WoW/MoM block-comparison pill for activity tiles (MAIN-04 Outbound Activity,
 * John 2026-07-11). Shows the signed delta of the current block vs the prior
 * block; the title attribute carries the two underlying totals. WoW = most recent
 * complete week vs the week before; MoM = last 4 complete weeks vs the prior 4.
 */
function ActivityDeltaPill({
  label,
  current,
  prior,
}: {
  label: string;
  current: number;
  prior: number;
}): React.JSX.Element {
  const diff = current - prior;
  const glyph = diff > 0 ? '▲' : diff < 0 ? '▼' : '—';
  const cls =
    diff > 0
      ? 'bg-good-bg text-good-text'
      : diff < 0
        ? 'bg-bad-bg text-bad-text'
        : 'bg-ink-100 text-ink-500';
  const amount = diff === 0 ? 'flat' : formatCountInt(Math.abs(diff));
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium ${cls}`}
      title={`${label}: ${formatCountInt(current)} vs ${formatCountInt(prior)}`}
    >
      <span className="text-ink-500">{label}</span>
      <span aria-hidden>{glyph}</span>
      <span className="tabular-nums">{amount}</span>
    </span>
  );
}

/**
 * MAIN-scoped weekly goal markLine values for weeklyBar tiles (quick-260630-wiw).
 * Rendered LOCALLY here as a dashed horizontal markLine, NOT via the shared
 * target-repo / migration path (which is out of scope for MAIN work). weeklyBar
 * tiles with no entry pass `undefined` → no markLine, preserving their look.
 *
 * quick-260701-dea FIX A: MAIN-01's 120 was REMOVED from this map. 120 is a
 * MONTHLY target, not per-week — a per-week 120 markLine dwarfed the ~9/week
 * bars. The plumbing is intentionally left intact for any future genuinely
 * per-week target. MAIN-01 now surfaces 120 via MAIN_MONTHLY_TARGET (a "% of
 * monthly target" chip + reference text) instead of a markLine.
 */
const WEEKLY_BAR_TARGET: Record<string, number> = {};

/**
 * MAIN-scoped MONTHLY goal values for weeklyBar tiles (quick-260701-dea FIX A).
 * The 120/month New-Meetings-Booked goal is named once here (not a magic
 * literal). The tile renders a "% of monthly target" chip = 30-day headline
 * total (data.total_value) ÷ goal, plus a "monthly goal: 120" reference line.
 * Mirrors the WEEKLY_BAR_TARGET pattern so both goal vocabularies live together.
 */
const MAIN_MONTHLY_TARGET: Record<string, number> = { 'MAIN-01': 120 };

/**
 * quick-260630-f7f Task 7 (item 10): MAIN-03 Opportunities Created — compute
 * % won + % open from the stage buckets ({ label: StageName, count } across ALL
 * stages created this fiscal quarter). % won = Closed Won / total; % open =
 * (NOT Closed Won/Lost) / total. Returns null when there is no countable total.
 */
function computeStageMix(
  buckets: MainGroupedResponse['buckets'],
): { pctWon: number; pctOpen: number; total: number } | null {
  let total = 0;
  let won = 0;
  let closed = 0; // won + lost
  for (const b of buckets) {
    const c = typeof b.count === 'number' ? b.count : 0;
    total += c;
    const stage = (b.label || '').toLowerCase();
    if (stage === 'closed won') {
      won += c;
      closed += c;
    } else if (stage === 'closed lost') {
      closed += c;
    }
  }
  if (total <= 0) return null;
  return { pctWon: won / total, pctOpen: (total - closed) / total, total };
}

/**
 * Format total_value for MainBucketedTile — count or currency per context.
 * The caller can pass a unit but for simplicity we use count by default.
 */
function formatTotal(value: number | null): string {
  if (value === null) return '—';
  if (value >= 1_000_000) return formatCurrency(value);
  // count / volume headline — thousands-separated INTEGER, no decimals
  // (quick-260626-j5u Task 1). e.g. 2740 → "2,740", 11900 → "11,900".
  return formatCountInt(value);
}

/**
 * Format a 'YYYY-MM' month-bucket label as compact 'M/YY' (quick-260625-nem
 * Task 9), e.g. '2026-06' → '6/26'. Non-matching labels pass through unchanged.
 */
function monthBucketLabel(label: string): string {
  const m = /^(\d{4})-(\d{2})$/.exec(label);
  if (!m) return label;
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- the regex has a mandatory capture group, so m[1] exists when m matched
  return `${String(Number(m[2]))}/${m[1]!.slice(-2)}`;
}

/**
 * Build a TRUE stacked-bar input from month buckets that carry a `stages` map
 * (MAIN-33 Marketing Pipeline Coverage, quick-260625-nem Task 9): X = month
 * (M/YY), one series per StageName so the bar stacks by deal stage. Only the
 * months present in the buckets are drawn (the recompute already drops empty
 * leading/trailing/interior months). Returns null when no bucket has `stages`.
 */
function buildStageStackInput(
  buckets: MainGroupedResponse['buckets'],
): { categories: string[]; series: { name: string; values: number[] }[] } | null {
  const hasStages = buckets.some(
    (b) => typeof b.stages === 'object' && b.stages != null,
  );
  if (!hasStages) return null;

  const categories = buckets.map((b) => monthBucketLabel(b.label));
  // Collect the full ordered set of stage names across all months.
  const stageNames: string[] = [];
  for (const b of buckets) {
    const stages = (b.stages ?? {}) as Record<string, number>;
    for (const s of Object.keys(stages)) {
      if (!stageNames.includes(s)) stageNames.push(s);
    }
  }
  const series = stageNames.map((name) => ({
    name,
    values: buckets.map((b) => {
      const stages = (b.stages ?? {}) as Record<string, number>;
      return typeof stages[name] === 'number' ? stages[name] : 0;
    }),
  }));
  return { categories, series };
}

/**
 * Derive ECharts input from the grouped response buckets.
 * The buckets array has { label, count?, value? } per MainBucketGroup.
 * For stacked-bar tiles the x-axis is the bucket labels (e.g. Status categories).
 * Each bucket represents one category across all weeks — simplify as single bar.
 */
function buildChartInput(
  buckets: MainGroupedResponse['buckets'],
  preferValue = false,
): {
  categories: string[];
  series: { name: string; values: number[] }[];
} {
  if (buckets.length === 0) {
    return { categories: [], series: [] };
  }

  const categories = buckets.map((b) => b.label);

  // quick-260625-nem Tasks 2 & 4: lineByQuarter tiles (MAIN-12/20) carry BOTH
  // `value` (the avg days / avg $ — what we plot) AND `count` (n opps). The
  // default count-first heuristic would wrongly chart the opp count, so these
  // tiles pass preferValue=true to plot the `value` field.
  if (preferValue) {
    const values = buckets.map((b) => (typeof b.value === 'number' ? b.value : 0));
    return { categories, series: [{ name: 'Value', values }] };
  }

  // If buckets have a `count` → single series of counts; otherwise plot `value`.
  const hasCounts = buckets.some((b) => typeof b.count === 'number');
  const values = buckets.map((b) =>
    typeof b.count === 'number' ? b.count : typeof b.value === 'number' ? b.value : 0,
  );

  return {
    categories,
    series: hasCounts
      ? [{ name: 'Count', values }]
      : [{ name: 'Value', values }],
  };
}

export function MainBucketedTile({
  metricId,
  title,
  chartType,
  tooltipFormula,
  drilldownLabel = 'View source records →',
  data,
  snapshots = [],
  isLoading,
  isError,
  error,
  refetch,
}: MainBucketedTileProps): React.JSX.Element {
  const tooltipContent = tooltipFormula ? (
    <>
      <p className="mb-1 font-semibold text-ink-900">{title}</p>
      <p>{tooltipFormula}</p>
    </>
  ) : (
    <p className="font-semibold text-ink-900">{title}</p>
  );

  // UX-07: cache-warming
  if (isError && error instanceof CacheNotReadyError) {
    return (
      <Card data-testid={`main-tile-${metricId}`}>
        <CardHeader>
          <CardTitle className="text-[0.72rem] uppercase tracking-wider font-semibold text-ink-600">
            {title}
          </CardTitle>
          {tooltipFormula && (
            <Tooltip trigger="?" label="Show definition">{tooltipContent}</Tooltip>
          )}
        </CardHeader>
        <CardContent>
          <div
            role="status"
            className="flex items-start gap-3 rounded-lg border border-warn bg-warn-bg px-4 py-3 text-sm text-warn-text"
            data-testid="cache-warming-banner"
          >
            <span
              aria-hidden
              className="mt-0.5 inline-flex h-5 w-5 flex-none items-center justify-center rounded-full bg-warn text-xs font-bold text-white"
            >
              ⌛
            </span>
            <div className="flex-1">
              <p className="font-semibold">Cache warming up</p>
              <p className="mt-1 text-xs text-warn-text/80">
                First data refresh expected within ~7 minutes.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  // UX-07: SF-unreachable
  if (isError && error instanceof SalesforceUnavailableError) {
    return (
      <Card data-testid={`main-tile-${metricId}`}>
        <CardHeader>
          <CardTitle className="text-[0.72rem] uppercase tracking-wider font-semibold text-ink-600">
            {title}
          </CardTitle>
          {tooltipFormula && (
            <Tooltip trigger="?" label="Show definition">{tooltipContent}</Tooltip>
          )}
        </CardHeader>
        <CardContent>
          <SfUnreachableBanner onRetry={refetch} />
        </CardContent>
      </Card>
    );
  }

  // Loading
  if (isLoading || !data) {
    return (
      <Card data-testid={`main-tile-${metricId}`}>
        <CardHeader>
          <CardTitle className="text-[0.72rem] uppercase tracking-wider font-semibold text-ink-600">
            {title}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3" aria-busy="true">
            <Skeleton className="h-8 w-20" data-testid="skeleton-total" />
            <Skeleton className="h-64 w-full" data-testid="skeleton-chart" />
            <Skeleton className="h-4 w-48" data-testid="skeleton-legend" />
          </div>
        </CardContent>
      </Card>
    );
  }

  // Placeholder branch
  if (data.placeholder_text) {
    return (
      <Card data-testid={`main-tile-${metricId}`}>
        <CardHeader>
          <CardTitle className="text-[0.72rem] uppercase tracking-wider font-semibold text-ink-600">
            {title}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-ink-700">{data.placeholder_text}</p>
        </CardContent>
      </Card>
    );
  }

  const buckets = data.buckets;

  // Empty state
  if (buckets.length === 0) {
    return (
      <Card data-testid={`main-tile-${metricId}`}>
        <CardHeader>
          <CardTitle className="text-[0.72rem] uppercase tracking-wider font-semibold text-ink-600">
            {title}
          </CardTitle>
          {tooltipFormula && (
            <Tooltip trigger="?" label="Show definition">{tooltipContent}</Tooltip>
          )}
        </CardHeader>
        <CardContent>
          <p
            className="py-8 text-center text-sm text-ink-500"
            data-testid="bucketed-no-data"
          >
            No data
          </p>
        </CardContent>
      </Card>
    );
  }

  // quick-260625-nem Tasks 2 & 4: unit-aware lineByQuarter tiles (MAIN-12/20)
  // plot the bucket `value` (avg $ / avg days), not the opp count.
  const quarterConfig = QUARTER_TILE_CONFIG[metricId];
  const isUnitQuarterTile = quarterConfig != null && chartType === 'lineByQuarter';

  // chartType is stackedBar/area/lineByQuarter/dailyBar/weeklyBar for bucketed
  // tiles (never 'sparkline').
  const effectiveChartType: 'stackedBar' | 'area' | 'lineByQuarter' | 'dailyBar' | 'weeklyBar' =
    chartType === 'sparkline' ? 'stackedBar' : chartType;

  // quick-260626-g28 Task 2 → weekly (quick-260626-j5u Task 3): activity series
  // (MAIN-04/05/06/07). X = day/week ('M/D'), Y = count; the headline is the
  // carried 30-day total and the context line reads "last 30 days". The per-week
  // buckets are NOT listed as a per-bucket HTML legend (noise) — the chart axis
  // carries them.
  const isDailyBar =
    effectiveChartType === 'dailyBar' || effectiveChartType === 'weeklyBar';

  // quick-260625-nem Task 9: month buckets carrying a `stages` map (MAIN-33)
  // render as a TRUE stacked bar — X = month (M/YY), one stack segment per
  // StageName. Only months present in the buckets are drawn.
  const stageStackInput =
    effectiveChartType === 'stackedBar' ? buildStageStackInput(buckets) : null;

  // quick-260630-f7f Task 5 (item 18) → rebuilt quick-260630-icb Task 3: MAIN-12
  // carries a SECOND series in each bucket's `openValue` — the POINT-IN-TIME
  // open-as-of-quarter-end avg — rendered as a second line (Closed-Won avg +
  // Open-as-of-quarter-end avg). Its values may be `null` (line gaps), so it
  // takes the dedicated lineByQuarter path below. Falls back to the single-series
  // path for older cache rows that carry no `openValue`.
  const dualSeriesInput = isUnitQuarterTile ? buildAvgDualSeriesInput(metricId, buckets) : null;

  // Build chart data for the single-series (number[]) paths; the dual-series path
  // (nullable) is handled separately so the non-line builders keep their number[]
  // contract. stage-stack overrides the default for stacked-bar tiles.
  const chartInputRaw = stageStackInput ?? buildChartInput(buckets, isUnitQuarterTile);
  // MAIN-26 Deal Slippage (2026-07-22): its buckets are 'YYYY-MM' push-event
  // months; the plain stacked-bar path would render the raw ISO label on the
  // x-axis. Format to compact 'M/YY' (matching the MAIN-33 stage-stack path) so
  // the slippage-over-time axis reads cleanly.
  const chartInput =
    metricId === 'MAIN-26' && !stageStackInput
      ? { ...chartInputRaw, categories: chartInputRaw.categories.map(monthBucketLabel) }
      : chartInputRaw;
  const lineByQuarterOpts: LineByQuarterOptions | undefined = isUnitQuarterTile
    ? { valueUnit: quarterConfig.valueUnit, yAxisName: quarterConfig.yAxisName }
    : undefined;
  const chartOption =
    effectiveChartType === 'lineByQuarter'
      ? buildEchartsOption(
          'lineByQuarter',
          dualSeriesInput ?? chartInput,
          undefined,
          lineByQuarterOpts,
        )
      : effectiveChartType === 'area'
        ? buildEchartsOption('area', chartInput)
        : effectiveChartType === 'dailyBar'
          ? buildEchartsOption('dailyBar', chartInput)
          : effectiveChartType === 'weeklyBar'
            ? // quick-260630-wiw: pass the MAIN-scoped weekly goal (MAIN-01 → 120)
              // so the dashed target markLine renders. Tiles with no WEEKLY_BAR_TARGET
              // entry (MAIN-04..07) get `undefined` → no markLine (unchanged look).
              buildEchartsOption('weeklyBar', chartInput, WEEKLY_BAR_TARGET[metricId])
            : buildEchartsOption('stackedBar', chartInput);

  // Headline: unit-aware for quarter tiles ("$49k" / "243.0 days"); otherwise
  // the default count/currency heuristic.
  const headlineText = isUnitQuarterTile
    ? quarterConfig.headline(data.total_value ?? 0)
    : formatTotal(data.total_value);

  // quick-260630-f7f Task 7 (item 10): MAIN-03 % open + % won stats from the
  // stage buckets. The WoW pill is NOT derivable — MAIN-03 is grouped, not
  // snap-backed, so a genuine prior-period scalar does not exist in the payload;
  // we render a marked TODO instead of fabricating a delta (follow-on: add
  // MAIN-03 to the snap set via a registry change, out of scope here).
  const stageMix = metricId === 'MAIN-03' ? computeStageMix(buckets) : null;

  // quick-260701-dea FIX B: WoW pill on snapshot-backed bucketed tiles. Uses the
  // SINGLE shared computeSnapDelta helper (lib/snap-delta.ts) — the SAME
  // algorithm MainTrendTile uses. Renders ONLY when the helper returns non-null
  // (≥2 distinct-dated real snapshots); tiles with an empty snapshot series
  // (every bucketed metric today — none is in SNAP_MAIN_PIPELINE_METRIC_IDS)
  // render NO chip, silently. Never fabricated.
  const snapDelta = computeSnapDelta(snapshots);
  let deltaChip: { label: string; cls: string; glyph: string } | null = null;
  if (snapDelta) {
    const pct = Math.round(Math.abs(snapDelta.ratio) * 100);
    if (snapDelta.flat) {
      deltaChip = { label: 'flat', cls: 'bg-ink-100 text-ink-500', glyph: '—' };
    } else {
      deltaChip = {
        label: `${String(pct)}% WoW`,
        cls: snapDelta.up ? 'bg-good-bg text-good-text' : 'bg-bad-bg text-bad-text',
        glyph: snapDelta.up ? '▲' : '▼',
      };
    }
  }

  // quick-260701-dea FIX A: MAIN-01 "% of monthly target" chip. 120 is a MONTHLY
  // goal (New Meetings Booked), NOT per-week — so instead of a per-week markLine
  // (removed above) we express pct = 30-day headline total (data.total_value) ÷
  // 120. Guarded to render only when total_value is a positive number.
  const monthlyGoal = MAIN_MONTHLY_TARGET[metricId];
  const monthlyPct =
    monthlyGoal != null &&
    typeof data.total_value === 'number' &&
    data.total_value > 0
      ? Math.round((data.total_value / monthlyGoal) * 100)
      : null;

  return (
    <Card data-testid={`main-tile-${metricId}`}>
      <CardHeader>
        <CardTitle className="text-[0.72rem] uppercase tracking-wider font-semibold text-ink-600">
          {title}
        </CardTitle>
        {tooltipFormula && (
          <Tooltip trigger="?" label="Show definition">{tooltipContent}</Tooltip>
        )}
      </CardHeader>
      <CardContent>
        {/* Total value + context line. The WoW pill (FIX B) renders inline next
            to the headline, matching MainTrendTile's wow-delta-chip visual, only
            when a real snapshot delta exists. */}
        <div className="flex flex-wrap items-baseline gap-x-2.5 gap-y-1">
          <p className="text-3xl font-bold tabular-nums text-ink-900">
            {headlineText}
          </p>
          {deltaChip && (
            <span
              className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11.5px] font-bold tabular-nums ${deltaChip.cls}`}
              data-testid="wow-delta-chip"
            >
              <span aria-hidden>{deltaChip.glyph}</span> {deltaChip.label}
            </span>
          )}
        </div>
        <p className="mt-0.5 text-xs text-ink-500">
          {isDailyBar ? 'last 30 days' : data.period_label}
        </p>

        {/* WoW/MoM block-comparison pills for the activity tiles (MAIN-04
            Outbound 2026-07-11; extended to MAIN-01/05/06/07 on 2026-07-13).
            Data-driven: present only when the recompute wrote `deltas`. */}
        {data.deltas && (
          <div
            className="mt-2 flex flex-wrap items-center gap-1.5"
            data-testid="activity-deltas"
          >
            <ActivityDeltaPill
              label="WoW"
              current={data.deltas.wow.current}
              prior={data.deltas.wow.prior}
            />
            <ActivityDeltaPill
              label="MoM"
              current={data.deltas.mom.current}
              prior={data.deltas.mom.prior}
            />
          </div>
        )}

        {/* quick-260701-dea FIX A: MAIN-01 monthly-target chip + reference text.
            The chip ("% of monthly target") is styled like the stage-mix chips;
            the reference line ("monthly goal: 120") uses the muted subtitle
            style so the 120 target is legible without the (removed) markLine. */}
        {monthlyPct != null && monthlyGoal != null && (
          <div
            className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1"
            data-testid="main-01-monthly-target"
          >
            <span className="inline-flex items-center rounded-full bg-brand-50 px-2.5 py-0.5 text-xs font-semibold tabular-nums text-brand-700">
              {monthlyPct}% of monthly target
            </span>
            <span className="text-xs text-ink-400">
              monthly goal: {formatCountInt(monthlyGoal)}
            </span>
          </div>
        )}
        {/* quick-260630-f7f Task 1 item 1: per-metric composition clarifier
            (MAIN-04 "= emails + calls + LinkedIn messages"). Math unchanged. */}
        {TILE_SUBTITLE_NOTE[metricId] && (
          <p className="mt-0.5 text-xs text-ink-400" data-testid="tile-subtitle-note">
            {TILE_SUBTITLE_NOTE[metricId]}
          </p>
        )}

        {/* quick-260630-f7f Task 7 item 10: MAIN-03 % open + % won chips +
            a marked TODO for the (non-fabricated) WoW pill. */}
        {stageMix && (
          <div
            className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1.5"
            data-testid="stage-mix"
          >
            <span className="inline-flex items-center rounded-full bg-good-bg px-2.5 py-0.5 text-xs font-semibold tabular-nums text-good-text">
              {(stageMix.pctWon * 100).toFixed(0)}% won
            </span>
            <span className="inline-flex items-center rounded-full bg-brand-50 px-2.5 py-0.5 text-xs font-semibold tabular-nums text-brand-700">
              {(stageMix.pctOpen * 100).toFixed(0)}% open
            </span>
            <span
              className="text-[11px] italic text-ink-400"
              data-testid="wow-todo-note"
            >
              WoW pill TODO — needs MAIN-03 snapshotting (follow-on)
            </span>
          </div>
        )}

        {/* ECharts chart — h-80 so X-axis labels (rotated when many categories)
            and Y-axis values both have full vertical room. Combined with the
            col-span-4 row width set in MainTileSection, marketing charts with
            15+ industries / sources / reasons have space to render every
            label. */}
        <div className="mt-3 h-72 w-full" data-testid="bucketed-chart-container">
          <ReactECharts
            option={chartOption}
            style={{ height: '100%', width: '100%' }}
            opts={{ renderer: 'canvas' }}
            notMerge
            lazyUpdate={false}
          />
        </div>

        {/* HTML legend — color squares + label + count. For the MAIN-12 dual
            line (quick-260630-f7f item 18) the legend distinguishes "Closed-Won
            avg" vs "Open-opp avg" keyed to the two series colors. For stage-
            stacked month tiles (MAIN-33) it lists the stack segments (stage
            names); otherwise one entry per bucket. Suppressed for dailyBar tiles
            (MAIN-05/06/07) — 31 daily entries would be noise. */}
        {!isDailyBar && (
          <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1">
            {(dualSeriesInput
              ? dualSeriesInput.series.map((s) => ({ label: s.name, count: undefined }))
              : stageStackInput
                ? stageStackInput.series.map((s) => ({ label: s.name, count: undefined }))
                : buckets
            ).map((item, i) => (
              <div
                key={item.label}
                className="flex items-center gap-1.5 text-xs"
                data-testid="bucketed-legend-item"
              >
                <span
                  aria-hidden
                  className="inline-block h-2 w-2 rounded-sm flex-none"
                  style={{ backgroundColor: stackColor(i) }}
                />
                <span className="text-ink-700">
                  {item.label}
                  {typeof item.count === 'number' && (
                    <span className="ml-0.5 tabular-nums text-ink-500">({formatCountInt(item.count)})</span>
                  )}
                </span>
              </div>
            ))}
          </div>
        )}

        {/* Drilldown placeholder — Phase 6 wires the real SF list-view link.
            Old code linked to '/sf-link/{metricId}' which the SPA router
            doesn't know and the api expects an SF record ID for. Click
            bounced to '/'. Matches CroTile pattern. */}
        <div className="mt-3 text-right">
          <Tooltip
            trigger={
              <span className="cursor-pointer text-xs font-medium text-brand-600 hover:text-brand-700 hover:underline">
                {drilldownLabel}
              </span>
            }
            label="Salesforce drilldown"
          >
            <p className="text-ink-700">Drilldown to Salesforce list view — Phase 6</p>
          </Tooltip>
        </div>
      </CardContent>
    </Card>
  );
}
